import type { ServicePageData } from "$lib/types/service-detail";

const aiRetouchMedia = {
  heroModelStripedKnit: {
    src: "/images/ai-retouching/ai-retouching-hero-fashion-model-striped-knit.webp",
    alt: "AI-generated fashion model in studio wearing a striped knit sweater and denim jeans with accessories",
    width: 1122,
    height: 1402,
  },
  heroGhostMannequinKnit: {
    src: "/images/ai-retouching/ai-retouching-hero-ghost-mannequin-knitwear.webp",
    alt: "Hollow ghost mannequin apparel presentation of striped knit sweater and denim jeans",
    width: 1122,
    height: 1402,
  },
  heroFlatlayKnit: {
    src: "/images/ai-retouching/ai-retouching-hero-flatlay-apparel-accessories.webp",
    alt: "Complete flat lay apparel styling of striped knit sweater, denim jeans, sneakers, and handbag",
    width: 1122,
    height: 1402,
  },
  introJewelry: {
    src: "/images/ai-retouching/ai-retouching-intro-coordinated-fashion-lookbook.webp",
    alt: "AI-styled model presenting a coordinated beige outfit with matching apparel and accessories",
    width: 1122,
    height: 1402,
  },
  comparisonCosmeticsBefore: {
    src: "/images/ai-retouching/ai-retouching-comparison-cosmetics-reference-before.webp",
    alt: "Terrycloth bucket bag reference arrangement with spa cosmetic products lineup",
    width: 1600,
    height: 2000,
  },
  comparisonCosmeticsAfter: {
    src: "/images/ai-retouching/ai-retouching-comparison-white-terrycloth-bag-cosmetics-after.webp",
    alt: "AI-generated white terrycloth bucket bag with authentic textile micro-fibers, matching cord, and studio cosmetic collection",
    width: 1600,
    height: 2000,
  },
  comparisonOriginal: {
    src: "/images/ai-retouching/ai-retouching-comparison-terrycloth-bag-prototype-before.webp",
    alt: "Physical product prototype with rope handle before generative AI terrycloth fabric synthesis",
    width: 1092,
    height: 1365,
  },
  showcaseVideo: {
    src: "/images/video-editing/commercial-hero-poster.webp",
    alt: "Editorial portrait surrounded by a digital video editing workflow",
    width: 1122,
    height: 1402,
  },
  showcaseBefore: {
    src: "/images/ai-retouching/ai-retouching-showcase-green-floral-dress-before.webp",
    alt: "Green floral summer dress flat lay garment before AI on-model generation",
    width: 1122,
    height: 1402,
  },
  showcaseAfter: {
    src: "/images/ai-retouching/ai-retouching-showcase-green-floral-dress-on-model-after.webp",
    alt: "Photorealistic AI fashion model presenting the green floral dress in a bright studio environment",
    width: 1122,
    height: 1402,
  },
  footwearSuedeFlatBefore: {
    src: "/images/ai-retouching/ai-retouching-footwear-beige-suede-ballet-flat-before.webp",
    alt: "Beige suede ballet flat footwear studio capture before retouching and prop removal",
    width: 1600,
    height: 2000,
  },
  footwearSuedeFlatAfter: {
    src: "/images/ai-retouching/ai-retouching-footwear-beige-suede-ballet-flat-after.webp",
    alt: "Finished beige suede ballet flat e-commerce packshot with clean profile shape, soft suede texture, and grounded shadow",
    width: 1084,
    height: 1355,
  },
} as const;

export const aiRetouchPage: ServicePageData = {
  slug: "ai-retouch",
  seo: {
    title: "AI Photo Retouching | Studio Click House",
    description:
      "AI-assisted photo retouching with human quality control for background generation, cleanup, and high-volume e-commerce image processing.",
  },
  hero: {
    title: "AI-Assisted",
    titleAccent: "Retouching.",
    theme: "light",
    description:
      "Accelerate fashion catalogs and apparel lookbooks with generative AI workflows. We transform flat lays and ghost mannequins into photorealistic on-model presentations with master human retouchers verifying every pixel.",
    media: aiRetouchMedia.heroModelStripedKnit,
    mediaFit: "cover",
    aspectRatio: "4/5",
    supportingMedia: [
      aiRetouchMedia.heroGhostMannequinKnit,
      aiRetouchMedia.heroFlatlayKnit,
    ],
  },
  intro: {
    heading: "What is human-in-the-loop AI retouching?",
    paragraphs: [
      "AI tools can introduce artifacts, invented details, and inconsistent color. Our AI-assisted workflow handles background replacement, bulk dust removal, and initial color passes; trained retouchers then check edges, skin texture, logos, and color before export.",
    ],
    stages: [
      {
        label: "Automated Neural Segmentation",
        description:
          "Custom AI models rapidly segment subjects, identify skin blemishes, and isolate complex foreground objects.",
        media: aiRetouchMedia.introJewelry,
      },
      {
        label: "Contextual Background Synthesis",
        description:
          "Generate hyper-realistic studio, lifestyle, or textured backdrops that automatically match subject lighting and shadows.",
      },
      {
        label: "Master Artist Quality Control",
        description:
          "Senior human retouchers inspect every image under high magnification, hand-correcting any anomalies before export.",
      },
    ],
  },
  beforeAfter: {
    heading: "Physical prototype to generative product packshot.",
    description:
      "Watch a rough cardboard cylinder prototype and taped cord handle transform into a production-ready white terrycloth bucket bag, generated with authentic textile micro-fibers, realistic volume, and soft studio contact shadows.",
    bullets: [
      "Generative 3D texture & micro-fiber textile synthesis",
      "Seamless integration with physical hardware & cord handles",
      "High-end commercial packshots without physical fabric sampling",
    ],
    beforeSrc: aiRetouchMedia.comparisonOriginal.src,
    beforeAlt: aiRetouchMedia.comparisonOriginal.alt,
    afterSrc: aiRetouchMedia.comparisonCosmeticsAfter.src,
    afterAlt: aiRetouchMedia.comparisonCosmeticsAfter.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    showLabels: true,
    width: 1600,
    height: 2000,
    layout: "cards",
    textPosition: "right",
    beforeCards: [
      {
        src: aiRetouchMedia.comparisonCosmeticsBefore.src,
        alt: aiRetouchMedia.comparisonCosmeticsBefore.alt,
        label: "Ref Set",
        width: 1600,
        height: 2000,
      },
      {
        src: aiRetouchMedia.comparisonOriginal.src,
        alt: aiRetouchMedia.comparisonOriginal.alt,
        label: "Prototype",
        width: 1092,
        height: 1365,
      },
    ],
  },
  showcase: {
    heading: "From flat garment to photorealistic on-model presentation",
    description:
      "Transform flat-lay and ghost mannequin dress photography into lifelike on-model catalog imagery. Our generative AI engine produces realistic fashion models wearing your exact garments with authentic drape, natural pose, and balanced studio lighting.",
    bullets: [
      "Flat apparel to photorealistic on-model styling",
      "Accurate fabric drape, pattern alignment & natural posture",
      "High-volume catalog lookbooks without on-location model shoots",
    ],
    theme: "light",
    beforeAfter: {
      before: {
        src: aiRetouchMedia.showcaseBefore.src,
        alt: aiRetouchMedia.showcaseBefore.alt,
        width: aiRetouchMedia.showcaseBefore.width,
        height: aiRetouchMedia.showcaseBefore.height,
        label: "Before",
      },
      after: {
        src: aiRetouchMedia.showcaseAfter.src,
        alt: aiRetouchMedia.showcaseAfter.alt,
        width: aiRetouchMedia.showcaseAfter.width,
        height: aiRetouchMedia.showcaseAfter.height,
        label: "After",
      },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "service-detail-bag-to-model",
      heading: "From isolated packshot to on-model lifestyle.",
      description:
        "Transform a standalone e-commerce handbag shot into high-fashion editorial lookbook imagery. Our generative AI engine produces a realistic fashion model wearing and carrying your product with natural grip, physical strap drape, and studio-accurate contact lighting.",
      bullets: [
        "Single product packshot transformed into on-model commercial scene",
        "Natural hand placement, authentic strap drape, and accurate shadow interaction",
        "Scale seasonal lookbooks and ad campaigns without booking live model shoots",
      ],
      beforeSrc: "/images/ai-retouching/ai-retouching-leather-handbag-packshot-before.webp",
      beforeAlt: "Brown leather handbag photographed against a clean studio background",
      afterSrc: "/images/ai-retouching/ai-retouching-model-with-leather-handbag-after.webp",
      afterAlt: "Generative AI fashion model carrying the brown leather handbag in studio lookbook",
      beforeLabel: "Packshot",
      afterLabel: "On-Model",
      showLabels: true,
      width: 1003,
      height: 1254,
      layout: "cards",
      textPosition: "right",
      comparisonHref: "#service-detail-features",
    },
    {
      sectionId: "ai-retouching-footwear-suede-flat-comparison",
      heading: "E-commerce footwear retouching & profile isolation",
      description:
        "Transform raw footwear captures into pristine e-commerce catalog packshots. We isolate the shoe profile, remove internal props and foot forms, preserve delicate suede nap texture, and generate clean grounded contact shadows.",
      bullets: [
        "Seamless prop and foot removal with hollow interior shaping",
        "Delicate suede nap, stitching, and sole contour preservation",
        "Marketplace-ready pure white background with grounded contact shadow",
      ],
      beforeSrc: aiRetouchMedia.footwearSuedeFlatBefore.src,
      beforeAlt: aiRetouchMedia.footwearSuedeFlatBefore.alt,
      afterSrc: aiRetouchMedia.footwearSuedeFlatAfter.src,
      afterAlt: aiRetouchMedia.footwearSuedeFlatAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      showLabels: true,
      width: aiRetouchMedia.footwearSuedeFlatAfter.width,
      height: aiRetouchMedia.footwearSuedeFlatAfter.height,
      layout: "cards",
      textPosition: "left",
    },
  ],
  features: {
    heading: "AI-assisted photo retouching with human review",
    items: [
      {
        icon: "Cpu",
        title: "Proprietary AI Architecture",
        description:
          "Our custom-trained neural models are tuned specifically on commercial product, jewelry, and fashion post-production datasets.",
      },
      {
        icon: "UserCheck",
        title: "100% Human Master Inspection",
        description:
          "Every single AI-processed asset is inspected and manually perfected by senior retouchers before leaving our studio.",
      },
      {
        icon: "Zap",
        title: "Sub-12 Hour Turnaround",
        description:
          "Slash turnaround times by up to 80% on massive seasonal volume drops, getting products onto storefronts days earlier.",
      },
      {
        icon: "Lock",
        title: "Air-Gapped Enterprise Security",
        description:
          "Your source media is processed on private, secure on-premise servers with strict NDA guarantees—never used for public model training.",
      },
      {
        icon: "Sparkles",
        title: "Realistic Shadow & Reflection Physics",
        description:
          "AI-synthesized scenes calculate accurate light falloff, ambient occlusion, and ground contact shadows tailored to the subject.",
      },
      {
        icon: "BadgePercent",
        title: "Cost-Efficient Bulk Rates",
        description:
          "Reduce manual work on high-volume catalogs and digital marketing batches.",
      },
    ],
  },
  productTypes: [
    {
      id: "editorial-model",
      code: "01",
      title: "Editorial Fashion Model",
      subtitle: "Full-body generative model presentation with authentic fabric drape and posture.",
      src: "/images/ai-retouching/ai-retouching-product-editorial-fashion-model.webp",
      alt: "Editorial fashion model generated with realistic styling and authentic studio lighting",
      fit: "cover",
    },
    {
      id: "skincare-products",
      code: "02",
      title: "Skincare Products",
      subtitle: "Topical bottles arranged as a coordinated product lineup.",
      src: "/images/ai-retouching/ai-retouching-product-skincare-serum-lineup.webp",
      alt: "Serum and spray skincare products arranged against a yellow background",
      fit: "cover",
    },
    {
      id: "quilted-handbag",
      code: "03",
      title: "Quilted Leather Handbag",
      subtitle: "Quilted leather accessory with refined texture and metal hardware.",
      src: "/images/portfolio/accessories-adele-black-leather-bag-02-after.webp",
      alt: "Black quilted leather handbag with gold hardware on a light background",
      fit: "cover",
    },
    {
      id: "emerald-dress",
      code: "04",
      title: "Emerald Mannequin Dress",
      subtitle: "Apparel shape and garment details presented without a visible mannequin.",
      src: "/images/ai-retouching/ai-retouching-product-ghost-mannequin-emerald-dress.webp",
      alt: "Emerald green dress presented on a ghost mannequin against a clean background",
      fit: "cover",
    },
    {
      id: "zebra-pump",
      code: "05",
      title: "Zebra Print Pump",
      subtitle: "Footwear retouching that preserves a bold patterned finish.",
      src: "/images/ai-retouching/ai-retouching-product-zebra-print-pointed-pump.png",
      alt: "Black and white zebra-print pointed-toe pump on a white background",
      fit: "cover",
    },
    {
      id: "feather-corset",
      code: "06",
      title: "Feather-Trim Corset",
      subtitle: "Detailed black corset styling with a textured feather trim.",
      src: "/images/ai-retouching/ai-retouching-product-black-feather-trim-corset.png",
      alt: "Black corset with feather trim photographed against a dark background",
      fit: "cover",
    },
  ],
  faqs: [
    {
      question: "How does human-in-the-loop AI retouching work?",
      answer:
        "AI-assisted tools handle background separation, bulk dust removal, and initial color alignment. Digital artists then review each file at high zoom and correct details such as fingers, hair, reflections, and logos.",
    },
    {
      question: "Are our client images used to train public AI models?",
      answer:
        "Never. All processing runs on our private, secure cloud infrastructure. Client files are protected by strict non-disclosure agreements (NDAs) and are never uploaded to public AI platforms or used for third-party model training.",
    },
    {
      question: "How does AI retouching compare in cost to manual retouching?",
      answer:
        "AI-assisted retouching typically reduces per-image production costs by 40% to 60% for high-volume batches (500+ images), while maintaining quality comparable to purely manual workflows.",
    },
    {
      question:
        "Can AI generate realistic lifestyle backgrounds for studio packshots?",
      answer:
        "Yes. We can place plain studio product photos into photorealistic kitchen, bathroom, outdoor, or luxury marble settings, matching the lighting angle, color temperature, and contact shadows.",
    },
    {
      question: "Can we test this with our own product batch?",
      answer:
        "Yes, send us up to 5 sample images, and our team will provide a complimentary test edit demonstrating both our AI processing speed and human QC polish.",
    },
  ],
  cta: {
    heading: "Supercharge your post-production pipeline",
    description:
      "Send us a sample batch. Experience the speed of next-gen AI coupled with the quality of master retouchers.",
    steps: [
      {
        title: "Upload sample files",
        description:
          "Share up to 5 images with your background or enhancement instructions.",
      },
      {
        title: "Inspect AI + QC results",
        description:
          "Review our turnaround and edge quality before committing to a full batch.",
      },
      {
        title: "Scale enterprise volumes",
        description:
          "Process massive seasonal catalog drops with sub-12-hour turnaround.",
      },
    ],
  },
};
